import React, { useRef, useState } from "react";
import { Heart, Archive } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * Swipeable list row wrapper.
 *   - Swipe LEFT  \u2192 reveals \"Archive\" action; release past threshold to fire onSwipeLeft().
 *   - Swipe RIGHT \u2192 reveals \"Favorite\" action; release past threshold to fire onSwipeRight().
 *
 * Works with touch + mouse. Falls back to no-op on non-supporting browsers.
 * The child renders normally; when idle the wrapper is transparent, so the
 * existing row visuals stay pixel-identical.
 *
 * Props:
 *   onSwipeLeft   \u2014 required for archive to work
 *   onSwipeRight  \u2014 required for favorite to work
 *   disabled      \u2014 boolean; disables all gestures
 *   threshold     \u2014 px past which action triggers (default 96)
 *   height        \u2014 approximate row height; used to size the reveal panels
 *   testId        \u2014 base testId for the wrapper
 */
export default function SwipeableRow({
  children,
  onSwipeLeft,
  onSwipeRight,
  disabled = false,
  threshold = 96,
  className,
  testId = "swipeable-row",
}) {
  const trackRef = useRef(null);
  const startX = useRef(0);
  const startY = useRef(0);
  const [dx, setDx] = useState(0);
  const [dragging, setDragging] = useState(false);
  const [locked, setLocked] = useState(false); // true if the gesture is committed to horizontal drag

  const clampMax = 160;

  const reset = (animate = true) => {
    if (animate && trackRef.current) {
      trackRef.current.style.transition = "transform 180ms ease";
    }
    setDx(0);
    setDragging(false);
    setLocked(false);
    // clear transition after animation completes
    setTimeout(() => {
      if (trackRef.current) trackRef.current.style.transition = "";
    }, 200);
  };

  const onStart = (clientX, clientY) => {
    if (disabled) return;
    startX.current = clientX;
    startY.current = clientY;
    setDragging(true);
    setLocked(false);
    if (trackRef.current) trackRef.current.style.transition = "";
  };

  const onMove = (clientX, clientY) => {
    if (!dragging) return;
    const rawDx = clientX - startX.current;
    const rawDy = clientY - startY.current;
    if (!locked) {
      // Determine primary axis with a small deadzone
      if (Math.abs(rawDx) < 8 && Math.abs(rawDy) < 8) return;
      if (Math.abs(rawDy) > Math.abs(rawDx)) {
        // Vertical scroll — abandon swipe
        setDragging(false);
        return;
      }
      setLocked(true);
    }
    const bounded = Math.max(-clampMax, Math.min(clampMax, rawDx));
    setDx(bounded);
  };

  const onEnd = () => {
    if (!dragging) return;
    if (dx <= -threshold && onSwipeLeft) {
      // fire archive
      onSwipeLeft();
    } else if (dx >= threshold && onSwipeRight) {
      onSwipeRight();
    }
    reset(true);
  };

  // Progress ratio for visual feedback
  const leftReveal = Math.max(0, -dx);   // 0..clampMax
  const rightReveal = Math.max(0, dx);
  const leftTrigger = leftReveal >= threshold;
  const rightTrigger = rightReveal >= threshold;

  return (
    <div
      className={cn("relative overflow-hidden select-none", className)}
      data-testid={testId}
    >
      {/* LEFT panel (archive) — revealed when swiping LEFT so panel sits on the RIGHT */}
      {onSwipeLeft && (
        <div
          className={cn(
            "absolute inset-y-0 right-0 flex items-center justify-end pr-4 pointer-events-none rounded-2xl",
            "transition-colors",
            leftTrigger ? "bg-amber-500" : "bg-amber-500/70"
          )}
          style={{ width: `${leftReveal}px`, opacity: leftReveal > 4 ? 1 : 0 }}
          aria-hidden
        >
          <div className="text-white text-[11px] font-medium inline-flex items-center gap-1">
            <Archive className="h-4 w-4" />
            {leftTrigger ? "Release" : "Archive"}
          </div>
        </div>
      )}

      {/* RIGHT panel (favorite) — revealed when swiping RIGHT so panel sits on the LEFT */}
      {onSwipeRight && (
        <div
          className={cn(
            "absolute inset-y-0 left-0 flex items-center justify-start pl-4 pointer-events-none rounded-2xl",
            "transition-colors",
            rightTrigger ? "bg-rose-500" : "bg-rose-500/70"
          )}
          style={{ width: `${rightReveal}px`, opacity: rightReveal > 4 ? 1 : 0 }}
          aria-hidden
        >
          <div className="text-white text-[11px] font-medium inline-flex items-center gap-1">
            <Heart className={cn("h-4 w-4", rightTrigger && "fill-current")} />
            {rightTrigger ? "Release" : "Favorite"}
          </div>
        </div>
      )}

      {/* Content track — moved horizontally as user drags */}
      <div
        ref={trackRef}
        onTouchStart={(e) => onStart(e.touches[0].clientX, e.touches[0].clientY)}
        onTouchMove={(e) => onMove(e.touches[0].clientX, e.touches[0].clientY)}
        onTouchEnd={onEnd}
        onTouchCancel={onEnd}
        onMouseDown={(e) => { if (e.button === 0) onStart(e.clientX, e.clientY); }}
        onMouseMove={(e) => { if (dragging) onMove(e.clientX, e.clientY); }}
        onMouseUp={onEnd}
        onMouseLeave={() => { if (dragging) onEnd(); }}
        style={{ transform: `translate3d(${dx}px, 0, 0)`, touchAction: "pan-y" }}
        data-testid={`${testId}-track`}
      >
        {children}
      </div>
    </div>
  );
}
